/**
 * Asserts that a value is never.
 *
 * This function is used to ensure exhaustive checking of discriminated unions
 * and is useful in switch statements to make sure you covered everything.
 *
 * Compared to the method of returning a `null` in switch statements and
 * ensuring the function returns `null` instead of `undefined`, this method is
 * more helpful in that it tells you what the unhandled values are.
 *
 * This version does not throw an error, but logs a warning to the console.
 */
export function assertNeverSafely(value: never, message: string = 'Unexpected value'): void {
  console.warn(message, value);
}

/**
 * Asserts that a value is never and also throws an error if it's not.
 */
export function assertNeverThrow(value: never, message: string = 'Unexpected value'): never {
  throw new Error(`${message}: ${JSON.stringify(value)}`);
}

/**
 * A helper type generic that takes two generics like `number` and `number`
 * and if they are the same type, the type generic returns the literal `true`.
 * If they are not the same type, the type generic returns the literal `false`.
 *
 * This is used by assertTypeEqual to assert that two types are equal.
 */

export type IsEqual<A, B, Y = true, N = false> =
  (<T>() => T extends A ? 1 : 2) extends <T>() => T extends B ? 1 : 2 ? Y : N;

/**
 * To assert that two types are equal (otherwise throws a type error) call
 * this function with the value of `true` and two generics which we want to
 * assert are equal.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
export function assertTypeEqual<A, B>(value: IsEqual<A, B>): void {}
